<template>
    <v-card class="inquiry-quick-review" ref="card">
        <v-container class="pa-xl-5" ref="container">
            <div class="header" ref="header">
                {{$vuetify.lang.t('$vuetify.inquiryQuickReview.inquiry')}}
            </div>
            <weekly-calendar
                ref="calendar"
                @load-data="getInquiries"
                :events="events"
            />
            <v-row>
                <v-col class="pt-0 inquires-container" :style="displayHeight">
                    <div
                        class="no-data"
                        v-if="laos.length === 0">
                        {{$vuetify.lang.t('$vuetify.inquiryQuickReview.inquiryMessage')}}{{ $util.formatDate(form.currentDate) }}
                    </div>
                    <div
                        v-else
                        v-for="(lao, index) in laos"
                        :key="`${index}${lao.id}`">
                        <v-row>
                            <v-col xl="2" lg="3">
                                <div class="profile">
                                    <img :src="getProfile(lao.image)">
                                </div>
                            </v-col>
                            <v-col
                                class="pl-0"
                                xl="3"
                                lg="2"
                                style="min-width: 0;">
                                <div class="lao-label lao-name locale-font">{{$vuetify.lang.t('$vuetify.inquiryQuickReview.name')}}</div>
                                <div class="lao-text">{{ lao.name }}</div>
                                <div class="lao-label lao-employee-no">{{$vuetify.lang.t('$vuetify.inquiryQuickReview.employeeNo')}}</div>
                                <div class="lao-text">{{ lao.employeeNo }}</div>
                            </v-col>
                            <v-col cols="7">
                                <v-row class="lao-label">{{$vuetify.lang.t('$vuetify.inquiryQuickReview.inquiryInTd')}}</v-row>
                                <v-row :class="statusClass(lao.laoType['key'])">
                                    <span>{{ lao.laoType['value'] }}</span>
                                    <div class="elipse"></div>
                                    <span>{{ lao.date }}</span>
                                    <div class="elipse"></div>
                                    <span>{{ lao.period['value'] }}</span>
                                </v-row>
                                <v-row v-if="permissionUpdate">
                                    <div class="choices-container" ref="statusContainer">
                                        <div
                                            :class="choiceClass('APV')"
                                            @click="changeStatus(lao, 'APV')">
                                            {{$vuetify.lang.t('$vuetify.inquiryQuickReview.approve')}}
                                        </div>
                                        <div
                                            :class="choiceClass('REJ')"
                                            @click="changeStatus(lao, 'REJ')">
                                            {{$vuetify.lang.t('$vuetify.inquiryQuickReview.reject')}}
                                        </div>
                                    </div>
                                </v-row>
                            </v-col>
                        </v-row>
                        <v-divider></v-divider>
                    </div>
                </v-col>
            </v-row>
        </v-container>
    </v-card>
</template>

<script>
import WeeklyCalendar from '@/components/WeeklyCalendar'
import permissionConstant from '@/constants/permissions.js'
import profileDefault from '@/assets/user_default.png'

export default {
    name: 'InquiryQuickReview',

    components: {
        WeeklyCalendar
    },

    data () {
        return {
            image: profileDefault,
            isLoading: false,
            branchId: null,
            color: {
                leave: '#aa1a11',
                onsite: '#ead2ac'
            },
            events: {},
            laos: [],
            form: {
                startDate: null,
                endDate: null,
                currentDate: null
            },
            laoListHeight: null,
            permissionUpdate: false
        }
    },

    computed: {
        displayHeight () {
            const style = { 'overflow-y': 'auto' }
            if (this.laoListHeight) {
                style['max-height'] = `${this.laoListHeight}px`
            }
            return style
        }
    },

    methods: {
        async getInquiries (payload) {
            if (!this.branchId) {
                return
            }
            this.$store.dispatch('setLoading', true)

            const form = this.getForm(payload)
            const res = await this.axios.get(
                `leave-or-onsite/reviewable?branch=${this.branchId}` +
                `&start=${form.startDate}&end=${form.endDate}&date=${form.currentDate}`
            )

            this.setListHeight()
            if (res && res.data && res.data.status === 'ok') {
                this.processCalendar(res.data.data.summary)
                this.laos = res.data.data.toReview
            }

            this.checkPermission()
            this.$store.dispatch('setLoading', false)
        },

        setListHeight () {
            const containerStyle = window.getComputedStyle(this.$refs.container, null)
            this.laoListHeight = this.$refs.card.$el.offsetHeight - (
                parseFloat(containerStyle.getPropertyValue('padding-top').substr(0, 2)) +
                parseFloat(containerStyle.getPropertyValue('padding-bottom').substr(0, 2)) +
                this.$refs.header.offsetHeight +
                this.$refs.calendar.$el.offsetHeight
            )
        },

        getForm (payload) {
            if (!payload) {
                payload = this.$refs.calendar.getData()
            }

            this.form = payload
            return payload
        },

        processCalendar (payload) {
            let counter = 0

            this.events = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] }
            for (const date in payload) {
                if (payload[date].hasLeave) {
                    this.events[counter].push(this.color.leave)
                }

                if (payload[date].hasOnsite) {
                    this.events[counter].push(this.color.onsite)
                }

                counter++
            }
        },

        getProfile (image) {
            if (!image) {
                return this.image
            }

            return image
        },

        statusClass (leaveType) {
            const base = 'inquiry-status-label'

            if (leaveType === 'ONS') {
                return `${base} text-yellow `
            }

            return `${base} text-red`
        },

        choiceClass (statusChoice) {
            const style = 'choice'
            if (this.status === statusChoice) {
                return `${style} active`
            }

            return style
        },

        async changeStatus (lao, status) {
            this.$store.dispatch('setLoading', true)
            const res = await this.axios.put(
                `leave-or-onsite/update-status/${lao.id}`,
                { status }
            )

            if (res && res.data && res.data.status === 'ok') {
                this.$notify({
                    title: this.$vuetify.lang.t('$vuetify.inquiryQuickReview.congratulation'),
                    text: `${this.$vuetify.lang.t('$vuetify.inquiryQuickReview.applicationStatus')} ${lao.name} ${this.$vuetify.lang.t('$vuetify.inquiryQuickReview.isUpdated')}.`,
                    type: 'success'
                })
            }

            // no dispatch loading to false because getInquiries will handle it
            await this.getInquiries()

            this.$emit('changeStatus')
        },

        checkPermission () {
            this.permissionUpdate = this.$util.checkPermission(permissionConstant.visitor.update)
        }
    },

    async mounted () {
        this.branchId = localStorage.getItem('currentBranchId')
        await this.getInquiries()
    },

    watch: {
        '$store.state.currentBranchId': async function (newValue) {
            if (newValue) {
                this.branchId = newValue
                await this.getInquiries()
            }
        },
        '$vuetify.lang.current': {
            handler: async function (after, before) {
                await this.getInquiries()
            },
            deep: true
        }
    }
}
</script>

<style lang="scss" scoped>
.inquiry-quick-review {
    width: 100%;
    height: 100%;
    border-radius: 15px !important;
    box-shadow: 0 3px 25px 0 rgba(0, 0, 0, 0.06) !important;

    .header {
        font-size: 24px !important;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.25;
        letter-spacing: normal;
        text-align: left;
        color: #000000;
    }

    .inquires-container {
        overflow-y: scroll;
    }

    .inquires-container::-webkit-scrollbar {
        width: 6px;
        background-color: #FFFFFF;
    }

    .inquires-container::-webkit-scrollbar-thumb {
        border-radius: 10px;
        background-color: #e5e5e5;
    }

    .inquires-container::-webkit-scrollbar-track {
        border-radius: 10px;
        background-color: #FFFFFF;
    }

    .profile {
        img {
            border-radius: 25px;
            width: 48px;
            height: 48px;
        }
    }

    @media only screen and (min-width: 1264px) and (max-width: 1904px) {
        .plr-lg-0 {
            padding-right: 0px;
            padding-left: 0px;
        }
    }

    .lao-label {
        font-size: 12px;
        line-height: 1.25;
        color: #6c6c6c;
    }

    .lao-text {
        font-size: 14px;
        line-height: 1.19;
        color: #131410;
        margin-top: 5px;
        margin-bottom: 14px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .inquiry-status-label {
        font-size: 14px;
    }
}

.choices-container {
    margin-top: 15px;
    display: inline-flex;

    .disable {
        pointer-events: none;
        cursor: not-allowed;
    }

    .choice {
        font-size: 14px;
        line-height: 1.29;
        height: 32px;
        min-width: 80px;
        width: 50%;
    }

    .choice:first-child {
        color: #6c98ba;
        border: solid 2px #6c98ba;
        border-top-left-radius: 25px;
        border-bottom-left-radius: 25px;

        &:hover {
            background-color: lighten($color: #6c98ba, $amount: 30);
        }

        &.active {
            color: white;
            background-color: #6c98ba;
        }
    }

    .choice:last-child {
        color: #ff544a;
        border: solid 2px #ff544a;
        border-top-right-radius: 25px;
        border-bottom-right-radius: 25px;

        &:hover {
            background-color: lighten($color: #ff544a, $amount: 30);
        }

        &.active {
            color: white;
            background-color: #ff544a;
        }
    }
}
.no-data {
    margin-top: 10rem;
    font-size: 22px;
    font-weight: 500;
    color: #6c6c6c;
    text-align: center;
}
.lao-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.lao-employee-no {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
